import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { FeatherIcon } from '../../FeatherIcon';

interface Props {
  accountId: string;
  url: string | null;
  alt?: string;
  /** Size class — default is a 96px square thumbnail. */
  className?: string;
  objectFit?: 'cover' | 'contain';
}

const MAX_CONCURRENT_THUMB_LOADS = 6;
const MAX_THUMB_RETRIES = 2;
const THUMB_RETRY_DELAYS_MS = [350, 1000] as const;
let activeThumbLoads = 0;
const thumbLoadQueue: Array<() => void> = [];

function runNextThumbLoad() {
  const next = thumbLoadQueue.shift();
  if (!next || activeThumbLoads >= MAX_CONCURRENT_THUMB_LOADS) return;
  activeThumbLoads += 1;
  next();
}

function enqueueThumbLoad(start: () => void): () => void {
  let cancelled = false;
  let started = false;
  let released = false;

  const run = () => {
    if (cancelled) {
      activeThumbLoads -= 1;
      runNextThumbLoad();
      return;
    }
    started = true;
    start();
  };

  if (activeThumbLoads < MAX_CONCURRENT_THUMB_LOADS) {
    activeThumbLoads += 1;
    run();
  } else {
    thumbLoadQueue.push(run);
  }

  return () => {
    if (released) return;
    released = true;
    cancelled = true;
    if (!started) {
      const idx = thumbLoadQueue.indexOf(run);
      if (idx >= 0) thumbLoadQueue.splice(idx, 1);
      return;
    }
    releaseThumbLoad();
  };
}

function releaseThumbLoad() {
  activeThumbLoads = Math.max(0, activeThumbLoads - 1);
  runNextThumbLoad();
}

export function VaultThumb({ accountId, url, alt = '', className, objectFit = 'cover' }: Props) {
  const { t } = useTranslation('sales');
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseLoadRef = useRef<(() => void) | null>(null);
  const cls = className || 'w-24 h-24';
  const proxiedUrl = useMemo(
    () => (url ? api.proxyMediaUrl(accountId, url) : null),
    [accountId, url]
  );

  useEffect(() => {
    retryCountRef.current = 0;
    setReloadToken(0);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [proxiedUrl]);

  useEffect(() => {
    setErrored(false);
    setLoaded(false);
    setSrc(null);
    if (!proxiedUrl) return undefined;

    const release = enqueueThumbLoad(() => setSrc(proxiedUrl));
    releaseLoadRef.current = release;

    return () => {
      release();
      if (releaseLoadRef.current === release) releaseLoadRef.current = null;
    };
  }, [proxiedUrl, reloadToken]);

  const finishLoad = () => {
    releaseLoadRef.current?.();
    releaseLoadRef.current = null;
    setLoaded(true);
  };

  const failLoad = () => {
    releaseLoadRef.current?.();
    releaseLoadRef.current = null;
    const retry = retryCountRef.current;
    if (retry < MAX_THUMB_RETRIES) {
      retryCountRef.current = retry + 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        setReloadToken((token) => token + 1);
      }, THUMB_RETRY_DELAYS_MS[retry] ?? 1000);
      return;
    }
    setErrored(true);
  };

  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  if (!url) {
    return (
      <div
        className={`${cls} flex items-center justify-center rounded-lg bg-zinc-800 text-zinc-500`}
        aria-label={t('vaultThumb.noPreview')}
      >
        <FeatherIcon name="image" size={20} />
      </div>
    );
  }

  if (errored) {
    return (
      <div
        className={`${cls} flex items-center justify-center rounded-lg bg-zinc-800 text-red-400`}
        title={t('vaultThumb.failedToLoad')}
      >
        <FeatherIcon name="alert-triangle" size={18} />
      </div>
    );
  }

  return (
    <div className={`${cls} relative rounded-lg overflow-hidden bg-zinc-900`}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-zinc-700/40" aria-hidden />
      )}
      <img
        src={src || undefined}
        alt={alt}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        onLoad={finishLoad}
        onError={failLoad}
        className={`w-full h-full ${objectFit === 'contain' ? 'object-contain' : 'object-cover'} transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}
